import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import NavbarLoggedInNew from "../Navbar-new/NavbarLoggedInNew";
import styles from "./QuestionLibrary.module.css";

const TYPE_CONFIG = {
  p: { label: "Programming", bg: "#e3f0f9", fg: "#0f6ca8" },
  s: { label: "Subjective", bg: "#f0ecfa", fg: "#6b4fb5" },
  o: { label: "MCQ", bg: "#e6f6ec", fg: "#1f8a4c" },
};

const DIFFICULTY_CONFIG = {
  E: { label: "Easy", fg: "#1f8a4c" },
  M: { label: "Medium", fg: "#b07d1a" },
  H: { label: "Hard", fg: "#c93c3c" },
};

const QuestionLibrary = () => {
  const navigate = useNavigate();

  // State
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({ type: "", difficulty: "", search: "" });
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadType, setUploadType] = useState("programming");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Assign modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [courses, setCourses] = useState([]);
  const [selectedCourseName, setSelectedCourseName] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null); // {type: 'success'|'error', message: string}

  // Get unique course names
  const uniqueCourseNames = [...new Set(courses.map((c) => c.name))];

  // Get students for selected course name
  const studentsForCourse = selectedCourseName
    ? courses.filter((c) => c.name === selectedCourseName)
    : [];

  // Get modules for selected student's course
  const modules = selectedStudent
    ? courses.find((c) => c.id === parseInt(selectedStudent))?.topics || []
    : [];

  // Fetch questions
  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.type) params.append("type", filter.type);
      if (filter.difficulty) params.append("difficulty", filter.difficulty);
      if (filter.search) params.append("search", filter.search);
      params.append("page", page);
      params.append("page_size", 50);

      const response = await api.get(`api/question-library/?${params}`);
      setQuestions(response.data.questions || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch questions:", err);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    setUploadResult(null);

    try {
      // Get presigned URL
      const presignedRes = await api.post("api/question-library/presigned-url/", {
        filename: uploadFile.name,
        content_type: "text/csv",
      });

      const { presigned_url, fields, file_key } = presignedRes.data;

      // Upload to GCS using PUT
      await fetch(presigned_url, {
        method: "PUT",
        body: uploadFile,
        headers: {
          "Content-Type": fields["Content-Type"] || "text/csv",
        },
      });

      // Import questions
      const importRes = await api.post("api/question-library/import/", {
        file_key,
        type: uploadType,
      });

      setUploadResult({
        success: true,
        message: importRes.data.message,
        created: importRes.data.created,
        errors: importRes.data.errors,
      });

      // Refresh list
      fetchQuestions();
    } catch (err) {
      console.error("Upload failed:", err);
      setUploadResult({
        success: false,
        message: err.response?.data?.error || "Upload failed",
      });
    } finally {
      setUploading(false);
    }
  };

  // Download template
  const handleDownloadTemplate = async (type) => {
    try {
      const response = await api.get(`api/question-library/template/${type}/`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${type}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  // Delete question
  const handleDelete = async (type, id) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;

    try {
      await api.delete(`api/question-library/${type}/${id}/`);
      fetchQuestions();
    } catch (err) {
      alert(err.response?.data?.error || "Delete failed");
    }
  };

  // Fetch mentor's course topics on page load
  useEffect(() => {
    const fetchMentorTopics = async () => {
      try {
        const response = await api.get("api/question-library/topics/");
        setCourses(response.data?.courses || []);
      } catch (err) {
        console.error("Failed to fetch topics:", err);
      }
    };
    fetchMentorTopics();
  }, []);

  // Open assign modal
  const handleOpenAssignModal = () => {
    setShowAssignModal(true);
    setSelectedCourseName("");
    setSelectedStudent("");
    setSelectedModule("");
    setAssignResult(null);
  };

  // Assign selected questions to module
  const handleAssignToModule = async () => {
    if (!selectedModule || selectedQuestions.length === 0) return;
    setAssigning(true);
    setAssignResult(null);
    try {
      const res = await api.post(`api/course/${selectedModule}/questions/assign/`, {
        questions: selectedQuestions,
      });
      setAssignResult({
        type: "success",
        message: res.data.message || `Assigned ${selectedQuestions.length} questions`,
      });
      setSelectedQuestions([]);
      fetchQuestions(); // Refresh to update "USED IN" column
      // Auto-close modal after 1.5s
      setTimeout(() => {
        setShowAssignModal(false);
        setAssignResult(null);
      }, 1500);
    } catch (err) {
      setAssignResult({
        type: "error",
        message: err.response?.data?.error || "Failed to assign questions",
      });
    } finally {
      setAssigning(false);
    }
  };

  // Toggle selection
  const toggleSelect = (q) => {
    setSelectedQuestions((prev) => {
      const key = `${q.type}_${q.id}`;
      const exists = prev.some((s) => `${s.type}_${s.id}` === key);
      if (exists) {
        return prev.filter((s) => `${s.type}_${s.id}` !== key);
      }
      return [...prev, { type: q.type, id: q.id }];
    });
  };

  const isSelected = (q) => {
    return selectedQuestions.some((s) => s.type === q.type && s.id === q.id);
  };

  return (
    <>
      <NavbarLoggedInNew />
      <div className={styles.container}>
        {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Question Library</h1>
          <span className={styles.count}>{total} questions</span>
          <span className={styles.subtitle}>Shared across every course</span>
        </div>
        <div className={styles.headerRight}>
          <button
            className={styles.templateBtn}
            onClick={() => setShowUpload(!showUpload)}
          >
            {showUpload ? "Cancel" : "Download template"}
          </button>
          <button
            className={styles.uploadBtn}
            onClick={() => setShowUpload(!showUpload)}
          >
            + Upload CSV
          </button>
        </div>
      </div>

      {/* Upload Panel */}
      {showUpload && (
        <div className={styles.uploadPanel}>
          <div className={styles.uploadDropzone}>
            <div className={styles.uploadIcon}>↑</div>
            <div className={styles.uploadContent}>
              <div className={styles.uploadTitle}>
                Drop a CSV to create questions in bulk
              </div>
              <div className={styles.uploadDesc}>
                One row per question. Test cases go in JSON format in the test_cases column.
              </div>
            </div>
            <div className={styles.uploadActions}>
              <select
                className={styles.typeSelect}
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
              >
                <option value="programming">Programming</option>
                <option value="subjective">Subjective</option>
                <option value="mcq">MCQ</option>
              </select>
              <label className={styles.browseBtn}>
                Browse files
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={(e) => setUploadFile(e.target.files[0])}
                />
              </label>
            </div>
          </div>

          <div className={styles.templatePanel}>
            <div className={styles.templateTitle}>Download templates</div>
            <div className={styles.templateLinks}>
              <button onClick={() => handleDownloadTemplate("programming")}>
                Programming CSV
              </button>
              <button onClick={() => handleDownloadTemplate("subjective")}>
                Subjective CSV
              </button>
              <button onClick={() => handleDownloadTemplate("mcq")}>
                MCQ CSV
              </button>
            </div>
          </div>

          {uploadFile && (
            <div className={styles.uploadFileInfo}>
              <span>Selected: {uploadFile.name}</span>
              <button
                className={styles.uploadStartBtn}
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Start Import"}
              </button>
            </div>
          )}

          {uploadResult && (
            <div
              className={`${styles.uploadResult} ${
                uploadResult.success ? styles.success : styles.error
              }`}
            >
              <div>{uploadResult.message}</div>
              {uploadResult.errors?.length > 0 && (
                <div className={styles.errorList}>
                  {uploadResult.errors.map((e, i) => (
                    <div key={i}>Row {e.row}: {e.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        {[
          { key: "", label: "All" },
          { key: "p", label: "Programming" },
          { key: "s", label: "Subjective" },
          { key: "o", label: "MCQ" },
        ].map((f) => (
          <button
            key={f.key}
            className={`${styles.filterChip} ${
              filter.type === f.key ? styles.active : ""
            }`}
            onClick={() => setFilter((prev) => ({ ...prev, type: f.key === prev.type ? "" : f.key }))}
          >
            {f.label}
          </button>
        ))}

        {["E", "M", "H"].map((d) => (
          <button
            key={d}
            className={`${styles.filterChip} ${
              filter.difficulty === d ? styles.active : ""
            }`}
            onClick={() => setFilter((prev) => ({ ...prev, difficulty: d === prev.difficulty ? "" : d }))}
          >
            {DIFFICULTY_CONFIG[d].label}
          </button>
        ))}

        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search by title, tag or ref"
          value={filter.search}
          onChange={(e) => setFilter((prev) => ({ ...prev, search: e.target.value }))}
        />
      </div>

      {/* Table */}
      <div className={styles.table}>
        <div className={styles.tableHeader}>
          <div className={styles.colCheck}></div>
          <div className={styles.colQuestion}>Question</div>
          <div className={styles.colType}>Type</div>
          <div className={styles.colDifficulty}>Difficulty</div>
          <div className={styles.colCases}>Cases</div>
          <div className={styles.colUsed}>Used in</div>
          <div className={styles.colActions}></div>
        </div>

        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : questions.length === 0 ? (
          <div className={styles.empty}>No questions found</div>
        ) : (
          questions.map((q) => {
            const typeConfig = TYPE_CONFIG[q.type] || TYPE_CONFIG.p;
            const diffConfig = DIFFICULTY_CONFIG[q.difficulty] || DIFFICULTY_CONFIG.M;
            const selected = isSelected(q);

            return (
              <div
                key={`${q.type}_${q.id}`}
                className={`${styles.tableRow} ${selected ? styles.selected : ""}`}
              >
                <div className={styles.colCheck}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleSelect(q)}
                  />
                </div>
                <div className={styles.colQuestion}>
                  <div className={styles.questionTitle}>{q.title}</div>
                  <div className={styles.questionMeta}>
                    {q.question_ref}
                    {q.tags?.length > 0 && ` · ${q.tags.join(", ")}`}
                  </div>
                </div>
                <div className={styles.colType}>
                  <span
                    className={styles.typeBadge}
                    style={{ background: typeConfig.bg, color: typeConfig.fg }}
                  >
                    {typeConfig.label}
                  </span>
                </div>
                <div className={styles.colDifficulty}>
                  <span style={{ color: diffConfig.fg, fontWeight: 600 }}>
                    {diffConfig.label}
                  </span>
                </div>
                <div className={styles.colCases}>{q.test_cases_count || "-"}</div>
                <div className={styles.colUsed}>
                  {q.used_in?.length > 0 ? q.used_in.join(", ") : "-"}
                </div>
                <div className={styles.colActions}>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => handleDelete(q.type, q.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {selectedQuestions.length > 0 && (
        <div className={styles.footer}>
          <span>{selectedQuestions.length} selected</span>
          <button
            className={styles.assignBtn}
            onClick={handleOpenAssignModal}
          >
            Assign to a module →
          </button>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAssignModal(false)}>
          <div className={styles.assignModal} onClick={(e) => e.stopPropagation()}>
            <h3>Assign {selectedQuestions.length} questions to module</h3>
            <p>Select a course, student, and module:</p>
            <label className={styles.selectLabel}>Course</label>
            <select
              className={styles.moduleSelect}
              value={selectedCourseName}
              onChange={(e) => {
                setSelectedCourseName(e.target.value);
                setSelectedStudent("");
                setSelectedModule("");
              }}
            >
              <option value="">Select a course...</option>
              {uniqueCourseNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <label className={styles.selectLabel}>Student</label>
            <select
              className={styles.moduleSelect}
              value={selectedStudent}
              onChange={(e) => {
                setSelectedStudent(e.target.value);
                setSelectedModule("");
              }}
              disabled={!selectedCourseName}
            >
              <option value="">Select a student...</option>
              {studentsForCourse.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.mentee || "No student assigned"}
                </option>
              ))}
            </select>
            <label className={styles.selectLabel}>Module</label>
            <select
              className={styles.moduleSelect}
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              disabled={!selectedStudent}
            >
              <option value="">Select a module...</option>
              {modules.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            {assignResult && (
              <div className={`${styles.assignResult} ${styles[assignResult.type]}`}>
                {assignResult.message}
              </div>
            )}
            <div className={styles.assignModalActions}>
              <button
                className={styles.modalCancelBtn}
                onClick={() => {
                  setShowAssignModal(false);
                  setAssignResult(null);
                }}
              >
                Cancel
              </button>
              <button
                className={styles.modalSaveBtn}
                onClick={handleAssignToModule}
                disabled={!selectedModule || assigning}
              >
                {assigning ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default QuestionLibrary;
